import { useState } from "react";
import { motion } from "framer-motion";
import { Send, LoaderCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[rgba(20,112,63,0.22)] rounded-sm px-4 py-3 text-sm text-[#12261A] placeholder-[#8CA594] focus:border-[#14703F] focus:outline-none transition-colors duration-300";

const GRADE_VALUES = ["A", "B", "C", "D"];
const THICKNESS_VALUES = ["0.35", "0.45", "0.55", "0.60"];
const PRODUCT_VALUES = ["gurjan", "eucalyptus", "okume"];

export const EnquiryForm = () => {
  const { t } = useLanguage();
  const f = t.form;
  const [sending, setSending] = useState(false);
  const [data, setData] = useState({
    name: "", company: "", phone: "", email: "",
    product: "gurjan", grade: "unsure", thickness: "unsure", quantity: "", message: "", website: "",
  });

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name.trim() || !data.phone.trim()) {
      toast.error(f.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...data,
        company: data.company || null,
        email: data.email || null,
        quantity: data.quantity || null,
        message: data.message || null,
      });
      toast.success(f.success);
      setData({ name: "", company: "", phone: "", email: "", product: "gurjan", grade: "unsure", thickness: "unsure", quantity: "", message: "", website: "" });
    } catch {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquiry" className="relative py-28 lg:py-40 bg-[#F1F7F1]" data-testid="enquiry-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <span className="eyebrow-label">{f.eyebrow}</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.02] text-[#12261A]">
              {f.titleA} <span className="italic text-[#14703F]">{f.titleItalic}</span>
            </h2>
            <p className="mt-8 max-w-md text-sm sm:text-base text-[#51685A] leading-relaxed">{f.intro}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              className="card-sheen bg-white border border-[rgba(20,112,63,0.2)] rounded-sm p-7 lg:p-10 grid grid-cols-1 sm:grid-cols-2 gap-4 shadow-[0_20px_60px_rgba(20,112,63,0.08)]"
              data-testid="enquiry-form"
            >
              <input type="text" value={data.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <input className={inputCls} placeholder={f.name} value={data.name} onChange={set("name")} data-testid="enquiry-name" />
              <input className={inputCls} placeholder={f.company} value={data.company} onChange={set("company")} data-testid="enquiry-company" />
              <input className={inputCls} placeholder={f.phone} value={data.phone} onChange={set("phone")} data-testid="enquiry-phone" />
              <input className={inputCls} type="email" placeholder={f.email} value={data.email} onChange={set("email")} data-testid="enquiry-email" />
              <select className={inputCls} value={data.product} onChange={set("product")} data-testid="enquiry-product">
                {PRODUCT_VALUES.map((v, i) => (
                  <option key={v} value={v}>{f.productOptions[i]}</option>
                ))}
              </select>
              <select className={inputCls} value={data.grade} onChange={set("grade")} data-testid="enquiry-grade">
                <option value="unsure">{f.grade} — {f.notSure}</option>
                {GRADE_VALUES.map((v, i) => (
                  <option key={v} value={v}>{f.gradeOptions[i]}</option>
                ))}
              </select>
              <select className={inputCls} value={data.thickness} onChange={set("thickness")} data-testid="enquiry-thickness">
                <option value="unsure">{f.thickness} — {f.notSure}</option>
                {THICKNESS_VALUES.map((v, i) => (
                  <option key={v} value={v}>{f.thicknessOptions[i]}</option>
                ))}
              </select>
              <input className={`${inputCls} sm:col-span-2`} placeholder={f.quantity} value={data.quantity} onChange={set("quantity")} data-testid="enquiry-quantity" />
              <textarea className={`${inputCls} sm:col-span-2 min-h-[110px] resize-y`} placeholder={f.message} value={data.message} onChange={set("message")} data-testid="enquiry-message" />
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit"
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#14703F] text-white px-7 py-4 font-mono-spec text-[11px] tracking-[0.2em] uppercase font-semibold hover:bg-[#0E5A32] transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? <LoaderCircle size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? f.submitting : f.submit}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
